import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import {
  FetchProductAppSubscriptionsForTransactionAccountQuery,
  FetchProductAppSubscriptionsForTransactionAccountQueryVariables,
  FetchProductAppSubscriptionsForBillingProfileQuery,
  FetchProductAppSubscriptionsForBillingProfileQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  fetchProductAppSubscriptionsForBillingProfile,
  fetchProductAppSubscriptionsForTransactionAccount,
} from '../../../product-app-subscriptions-container/src/queries/fetch-product-app-susbcriptions-txa';

export const useFetchNonCloudSiteLinkedEntitlements = ({
  transactionAccountId,
  billingProfileId,
}: {
  transactionAccountId?: string;
  billingProfileId?: string;
}) => {
  const filterOptions = { key: 'isNull', value: 'cloudSiteId' };

  const {
    data: productsAndAppsForTransactionAccount,
    loading: productsAndAppsForTransactionAccountLoading,
    error: productsAndAppsForTransactionAccountError,
    refetch: refetchForTransactionAccount,
  } = useQuery<
    FetchProductAppSubscriptionsForTransactionAccountQuery,
    FetchProductAppSubscriptionsForTransactionAccountQueryVariables
  >(fetchProductAppSubscriptionsForTransactionAccount, {
    variables: {
      id: transactionAccountId as string,
      filterOptions,
    },
    skip: !transactionAccountId,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  });

  const {
    data: productsAndAppsForBillingProfile,
    loading: productsAndAppsForBillingProfileLoading,
    error: productsAndAppsForBillingProfileError,
    refetch: refetchForBillingProfile,
  } = useQuery<
    FetchProductAppSubscriptionsForBillingProfileQuery,
    FetchProductAppSubscriptionsForBillingProfileQueryVariables
  >(fetchProductAppSubscriptionsForBillingProfile, {
    variables: {
      id: billingProfileId as string,
      filterOptions,
    },
    skip: !billingProfileId,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  });

  const data = useMemo(
    () =>
      transactionAccountId
        ? productsAndAppsForTransactionAccount?.transactionAccount?.productAndAppSubscriptions
        : productsAndAppsForBillingProfile?.invoiceGroup?.productAndAppSubscriptions,
    [transactionAccountId, productsAndAppsForTransactionAccount, productsAndAppsForBillingProfile]
  );

  const isEmptyUnprovisionedData = useMemo(() => {
    const unprovisionedData = (data as Record<string, any>) || {};

    const sumTotalProducts = (result: number, currentKey: string) => {
      const products = unprovisionedData[currentKey]?.products || [];
      result += products.length;

      return result;
    };

    const totalProducts = Object.keys(unprovisionedData).reduce(sumTotalProducts, 0);

    return totalProducts === 0;
  }, [data]);

  return {
    data,
    loading: productsAndAppsForTransactionAccountLoading || productsAndAppsForBillingProfileLoading,
    error: productsAndAppsForTransactionAccountError || productsAndAppsForBillingProfileError,
    refetch: refetchForTransactionAccount || refetchForBillingProfile,
    isEmptyUnprovisionedData,
  };
};
